import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import emailService from './emailService';

/**
 * Service for managing parent accounts and parent-child relationships
 */

/**
 * Link a parent account to a child account
 * @param {string} parentId - Parent user ID
 * @param {string} childEmail - Child's email address
 * @returns {Promise<Object>} Link result with success status and message
 */
export const linkParentToChild = async (parentId, childEmail) => {
  try {
    // Find child by email
    const usersQuery = query(
      collection(db, 'users'),
      where('email', '==', childEmail.toLowerCase().trim()),
      where('type', '==', 'student')
    );
    const querySnapshot = await getDocs(usersQuery);

    if (querySnapshot.empty) {
      return {
        success: false,
        message: 'No student account found with this email address.'
      };
    }

    const childDoc = querySnapshot.docs[0];
    const childId = childDoc.id;
    const childData = childDoc.data();

    // Check if parent is already linked
    const parentRef = doc(db, 'users', parentId);
    const parentSnap = await getDoc(parentRef);
    const parentData = parentSnap.data() || {};

    const linkedChildren = parentData.linkedChildren || [];
    if (linkedChildren.includes(childId)) {
      return {
        success: false,
        message: 'This child is already linked to your account.'
      };
    }

    // Add child to parent's linkedChildren array
    await updateDoc(parentRef, {
      linkedChildren: [...linkedChildren, childId],
      updatedAt: Timestamp.now()
    });

    // Add parent to child's linkedParents array
    const childRef = doc(db, 'users', childId);
    const childSnap = await getDoc(childRef);
    const childDataFull = childSnap.data() || {};
    const linkedParents = childDataFull.linkedParents || [];
    
    if (!linkedParents.includes(parentId)) {
      await updateDoc(childRef, {
        linkedParents: [...linkedParents, parentId],
        updatedAt: Timestamp.now()
      });
    }

    // Send notification email to child's email (if different from parent)
    if (childData.email && childData.email !== parentData.email) {
      try {
        await emailService.sendParentLinkNotification(
          childData.email,
          childData.name || 'Student',
          parentData.name || 'Parent'
        );
      } catch (emailError) {
        console.warn('Failed to send parent link notification:', emailError);
        // Don't fail the linking process if email fails
      }
    }

    return {
      success: true,
      message: `Successfully linked to ${childData.name || childEmail}'s account.`,
      childId,
      childName: childData.name || childEmail
    };
  } catch (error) {
    console.error('Error linking parent to child:', error);
    return {
      success: false,
      message: 'Failed to link account. Please try again.'
    };
  }
};

/**
 * Get all children linked to a parent
 * @param {string} parentId - Parent user ID
 * @returns {Promise<Array>} Array of child user objects
 */
export const getLinkedChildren = async (parentId) => {
  try {
    const parentRef = doc(db, 'users', parentId);
    const parentSnap = await getDoc(parentRef);
    
    if (!parentSnap.exists()) {
      return [];
    }

    const parentData = parentSnap.data();
    const linkedChildren = parentData.linkedChildren || [];

    if (linkedChildren.length === 0) {
      return [];
    }

    // Fetch all child documents
    const childrenPromises = linkedChildren.map(childId => 
      getDoc(doc(db, 'users', childId))
    );
    const childrenSnaps = await Promise.all(childrenPromises);

    return childrenSnaps
      .filter(snap => snap.exists())
      .map(snap => ({
        id: snap.id,
        ...snap.data()
      }));
  } catch (error) {
    console.error('Error getting linked children:', error);
    return [];
  }
};

/**
 * Get child's progress summary
 * @param {string} childId - Child user ID
 * @returns {Promise<Object>} Child progress data
 */
export const getChildProgress = async (childId) => {
  try {
    const childRef = doc(db, 'users', childId);
    const childSnap = await getDoc(childRef);

    if (!childSnap.exists()) {
      return null;
    }

    const childData = childSnap.data();

    // Get total points from child data
    const totalPoints = childData.totalPoints || 0;

    // Get badges
    const badgesRef = collection(db, 'users', childId, 'badges');
    const badgesSnapshot = await getDocs(badgesRef);
    const badges = badgesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get registered sessions
    const sessionsRef = collection(db, 'users', childId, 'studentSessions');
    const sessionsSnapshot = await getDocs(sessionsRef);
    const registeredSessions = sessionsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get attendance data
    const attendanceRef = collection(db, 'users', childId, 'sessionAttendance');
    const attendanceSnapshot = await getDocs(attendanceRef);
    const attendanceRecords = attendanceSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate attendance streak
    const attendanceStreak = childData.attendanceStreak || 0;

    return {
      childId,
      childName: childData.name || childData.email?.split('@')[0] || 'Student',
      childEmail: childData.email,
      totalPoints,
      badges: badges.length,
      badgeDetails: badges,
      registeredSessions: registeredSessions.length,
      attendanceStreak,
      attendanceRecords: attendanceRecords.length,
      lastActive: childData.updatedAt || childData.createdAt
    };
  } catch (error) {
    console.error('Error getting child progress:', error);
    return null;
  }
};

/**
 * Subscribe to child's progress updates
 * @param {string} parentId - Parent user ID
 * @param {string} childId - Child user ID
 * @param {Function} callback - Callback function to receive updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToChildProgress = (parentId, childId, callback) => {
  const childRef = doc(db, 'users', childId);
  
  return onSnapshot(childRef, async (snapshot) => {
    if (snapshot.exists()) {
      const progress = await getChildProgress(childId);
      callback(progress);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('Error subscribing to child progress:', error);
    callback(null);
  });
};

/**
 * Unlink a child from parent account
 * @param {string} parentId - Parent user ID
 * @param {string} childId - Child user ID
 * @returns {Promise<Object>} Unlink result
 */
export const unlinkChild = async (parentId, childId) => {
  try {
    // Remove child from parent's linkedChildren
    const parentRef = doc(db, 'users', parentId);
    const parentSnap = await getDoc(parentRef);
    const parentData = parentSnap.data() || {};
    const linkedChildren = (parentData.linkedChildren || []).filter(id => id !== childId);
    
    await updateDoc(parentRef, {
      linkedChildren,
      updatedAt: Timestamp.now()
    });

    // Remove parent from child's linkedParents
    const childRef = doc(db, 'users', childId);
    const childSnap = await getDoc(childRef);
    const childData = childSnap.data() || {};
    const linkedParents = (childData.linkedParents || []).filter(id => id !== parentId);
    
    await updateDoc(childRef, {
      linkedParents,
      updatedAt: Timestamp.now()
    });

    return {
      success: true,
      message: 'Child account unlinked successfully.'
    };
  } catch (error) {
    console.error('Error unlinking child:', error);
    return {
      success: false,
      message: 'Failed to unlink account. Please try again.'
    };
  }
};

/**
 * Get child's homework submissions
 * @param {string} childId - Child user ID
 * @returns {Promise<Array>} Array of homework submissions
 */
export const getChildHomework = async (childId) => {
  try {
    // This would need to query homework submissions
    // For now, return empty array - this can be enhanced based on your homework structure
    return [];
  } catch (error) {
    console.error('Error getting child homework:', error);
    return [];
  }
};

/**
 * Get child's upcoming sessions and homework due dates
 * @param {string} childId - Child user ID
 * @returns {Promise<Object>} Upcoming events
 */
export const getChildUpcomingEvents = async (childId) => {
  try {
    // Get registered sessions
    const sessionsRef = collection(db, 'users', childId, 'studentSessions');
    const sessionsSnapshot = await getDocs(sessionsRef);
    const sessionIds = sessionsSnapshot.docs.map(doc => doc.id);

    // Get session details
    const sessions = [];
    for (const sessionId of sessionIds) {
      const sessionRef = doc(db, 'sessions', sessionId);
      const sessionSnap = await getDoc(sessionRef);
      if (sessionSnap.exists()) {
        sessions.push({
          id: sessionSnap.id,
          ...sessionSnap.data()
        });
      }
    }

    // Filter upcoming sessions (start date in future)
    const upcomingSessions = sessions.filter(session => {
      const startDate = session.startDate?.toDate();
      return startDate && startDate > new Date();
    }).sort((a, b) => {
      const aDate = a.startDate?.toDate() || new Date();
      const bDate = b.startDate?.toDate() || new Date();
      return aDate - bDate;
    });

    return {
      upcomingSessions: upcomingSessions.slice(0, 5), // Next 5 sessions
      totalSessions: sessions.length
    };
  } catch (error) {
    console.error('Error getting child upcoming events:', error);
    return {
      upcomingSessions: [],
      totalSessions: 0
    };
  }
};

